(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const parseJson = (node) => {
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  };

  const config = parseJson(qs('[data-oss-product-config]')) || {};
  const product = parseJson(qs('[data-product-json]')) || null;

  const normalise = (value) => String(value == null ? '' : value).trim();
  const lower = (value) => normalise(value).toLowerCase();
  const tags = Array.isArray(config.tags) ? config.tags.map(lower) : [];
  const type = lower(config.type || (product && product.type));
  const isJersey = type.includes('jersey') || tags.includes('jersey') || tags.includes('jerseys') || tags.includes('football jersey');

  const formatMoney = (amount) => {
    const num = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: config.currency || 'USD' }).format(num);
    } catch (e) {
      return `$${num.toFixed(2)}`;
    }
  };

  const deriveTeamName = () => {
    if (normalise(config.teamName)) return normalise(config.teamName);
    let name = normalise(config.title || (product && product.title));
    name = name
      .replace(/^(adidas|nike|puma)\s+/i, '')
      .replace(/\b(19|20)\d{2}\s*[\/-]\s*\d{2,4}\b/gi, '')
      .replace(/\b(home|away|third|fourth|authentic|replica|soccer|football|jersey|shirt|kit)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return name || 'Club';
  };

  const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 3).map(part => part[0]).join('').toUpperCase();

  const textColorFor = (background) => {
    const hex = normalise(background).replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(hex)) return '#fff';
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.7 ? '#111' : '#fff';
  };

  const addClubBanner = () => {
    const grid = qs('.product-layout-grid');
    if (!grid) return;
    const scope = grid.parentElement || document;
    if (qs('.oss-club-banner', scope)) return;

    const team = deriveTeamName();
    const banner = document.createElement('div');
    banner.className = 'oss-club-banner';
    banner.style.setProperty('--oss-team-color', normalise(config.teamColor) || '#111111');
    banner.style.setProperty('--oss-team-text', textColorFor(config.teamColor || '#111111'));
    banner.setAttribute('aria-label', team);

    const logo = document.createElement('span');
    logo.className = 'oss-club-logo';
    if (normalise(config.teamLogoUrl)) {
      const img = document.createElement('img');
      img.src = config.teamLogoUrl;
      img.alt = `${team} crest`;
      img.loading = 'eager';
      img.decoding = 'async';
      logo.appendChild(img);
    } else {
      logo.classList.add('oss-club-logo--initials');
      logo.textContent = initials(team);
      logo.setAttribute('aria-hidden', 'true');
    }

    const name = document.createElement('span');
    name.className = 'oss-club-name';
    name.textContent = team;
    banner.append(logo, name);

    /* Important: keep the banner OUTSIDE the theme's two-column product grid. */
    grid.insertAdjacentElement('beforebegin', banner);
  };

  const addMobileTitle = () => {
    const grid = qs('.product-layout-grid');
    const original = qs('.product-detail__title');
    if (!grid || !original || qs('.oss-jersey-mobile-title', grid)) return;
    const wrap = document.createElement('div');
    wrap.className = 'oss-jersey-mobile-title';
    const h1 = document.createElement('h1');
    h1.textContent = normalise(original.textContent);
    wrap.appendChild(h1);
    const media = qs('.product-layout-grid__images', grid);
    if (media) grid.insertBefore(wrap, media);
    else grid.insertBefore(wrap, grid.firstChild);
  };

  const isUnavailable = (node) => {
    if (!node) return false;
    return node.disabled || node.getAttribute('aria-disabled') === 'true' || node.classList.contains('is-unavailable') || node.classList.contains('opt-unavailable') || node.classList.contains('unavailable');
  };

  const buildSizePicker = () => {
    const selectors = qsa('.option-selector[data-option]');
    const source = selectors.find(el => ['size', 'größe', 'groesse', 'talla'].includes(lower(el.dataset.option)));
    if (!source || qs('.oss-size-picker')) return;

    const dropdownOptions = qsa('.custom-select__option[data-value]', source).filter(el => normalise(el.dataset.value));
    const listedInputs = qsa('input.opt-btn, input[type="radio"]', source).filter(input => normalise(input.value));
    if (!dropdownOptions.length && !listedInputs.length) return;

    const picker = document.createElement('div');
    picker.className = 'oss-size-picker';
    const head = document.createElement('div');
    head.className = 'oss-size-head';
    const label = document.createElement('div');
    label.className = 'oss-size-label';
    label.textContent = 'Size';
    head.appendChild(label);

    const existingGuide = qs('.js-size-chart-open', source) || qs('.js-size-chart-open');
    if (existingGuide || normalise(config.sizeGuideUrl)) {
      const guide = document.createElement('button');
      guide.type = 'button';
      guide.className = 'oss-size-guide';
      guide.textContent = 'Size Guide';
      guide.addEventListener('click', () => {
        if (existingGuide) existingGuide.click();
        else window.location.href = config.sizeGuideUrl;
      });
      head.appendChild(guide);
    }

    const grid = document.createElement('div');
    grid.className = 'oss-size-grid';
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', 'Size');
    picker.append(head, grid);
    source.parentNode.insertBefore(picker, source);
    source.classList.add('oss-original-size-selector');

    const sourceNodes = dropdownOptions.length ? dropdownOptions : listedInputs;
    const buttons = [];

    const isSelected = (node) => dropdownOptions.length ? node.getAttribute('aria-selected') === 'true' : !!node.checked;
    const displayValue = (node) => dropdownOptions.length ? normalise(node.dataset.value || node.textContent) : normalise(node.value);

    const sync = () => {
      sourceNodes.forEach((node, index) => {
        const btn = buttons[index];
        if (!btn) return;
        const selected = isSelected(node);
        const unavailable = isUnavailable(node);
        btn.classList.toggle('is-selected', selected);
        btn.classList.toggle('is-unavailable', unavailable);
        btn.disabled = unavailable;
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    };

    sourceNodes.forEach((node) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'oss-size-btn';
      btn.textContent = displayValue(node);
      btn.addEventListener('click', () => {
        if (isUnavailable(node)) return;
        if (dropdownOptions.length) {
          node.click();
        } else {
          const forLabel = node.id ? qs(`label[for="${CSS.escape(node.id)}"]`, source) : null;
          if (forLabel) forLabel.click(); else node.click();
        }
        window.setTimeout(sync, 0);
        window.setTimeout(sync, 120);
      });
      grid.appendChild(btn);
      buttons.push(btn);
    });

    source.addEventListener('change', sync);
    new MutationObserver(sync).observe(source, { subtree: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'aria-disabled', 'checked', 'disabled'] });
    sync();
  };

  const patchIcon = (index) => index === 0
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="m12 5 1.7 4 4.3.3-3.3 2.8 1 4.2-3.7-2.2-3.7 2.2 1-4.2L6 9.3 10.3 9 12 5Z" fill="none" stroke="currentColor" stroke-width="1.3"/></svg>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 19 6v5c0 4.8-2.8 8-7 10-4.2-2-7-5.2-7-10V6l7-3Z" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="m9 12 2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.7"/></svg>';

  const currentVariant = () => {
    if (!product || !Array.isArray(product.variants)) return null;
    const addButton = qs('button[data-add-to-cart]');
    const form = addButton && addButton.closest('form');
    const idInput = form && qs('input[name="id"]', form);
    const id = idInput ? String(idInput.value) : '';
    return product.variants.find(variant => String(variant.id) === id) || product.variants[0] || null;
  };

  const variantPrice = () => {
    const variant = currentVariant();
    if (!variant) return 0;
    const cents = Number(variant.price || 0);
    return Number.isFinite(cents) ? cents / 100 : 0;
  };

  const buildPatchPanel = () => {
    if (qs('.oss-patch-panel')) return;
    const addButton = qs('button[data-add-to-cart]');
    const form = addButton && addButton.closest('form');
    const anchor = qs('.oss-size-picker') || qs('.product-detail__options');
    if (!form || !anchor) return;

    const patches = Array.isArray(config.patches) ? config.patches.filter(p => p && normalise(p.name)) : [];
    if (!patches.length) return;

    const panel = document.createElement('div');
    panel.className = 'oss-patch-panel';
    const heading = document.createElement('div');
    heading.className = 'oss-patch-heading';
    heading.textContent = 'Patches';
    panel.appendChild(heading);

    const selections = [];
    patches.slice(0, 2).forEach((patch, index) => {
      const row = document.createElement('label');
      row.className = 'oss-patch-row';
      const left = document.createElement('span');
      left.className = 'oss-patch-left';
      const icon = document.createElement('span');
      icon.className = 'oss-patch-icon';
      icon.innerHTML = patchIcon(index);
      const copy = document.createElement('span');
      copy.className = 'oss-patch-copy';
      const strong = document.createElement('strong');
      strong.textContent = patch.name;
      copy.appendChild(strong);
      const price = Number(patch.price || 0);
      if (price > 0) {
        const small = document.createElement('small');
        small.textContent = `+${formatMoney(price)}`;
        copy.appendChild(small);
      }
      left.append(icon, copy);

      const switchWrap = document.createElement('span');
      switchWrap.className = 'oss-switch';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.setAttribute('role', 'switch');
      checkbox.setAttribute('aria-label', patch.name);
      const track = document.createElement('span');
      track.className = 'oss-switch-track';
      switchWrap.append(checkbox, track);
      row.append(left, switchWrap);
      panel.appendChild(row);

      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = `properties[${patch.name}]`;
      hidden.value = 'Yes';
      hidden.disabled = true;
      form.appendChild(hidden);

      const priceHidden = document.createElement('input');
      priceHidden.type = 'hidden';
      priceHidden.name = `properties[${patch.name} surcharge]`;
      priceHidden.value = price > 0 ? formatMoney(price) : 'Included / not configured';
      priceHidden.disabled = true;
      form.appendChild(priceHidden);

      checkbox.addEventListener('change', () => {
        hidden.disabled = !checkbox.checked;
        priceHidden.disabled = !checkbox.checked;
        updateTotal();
      });
      selections.push({ checkbox, price });
    });

    let totalNode = null;
    if (selections.some(item => item.price > 0)) {
      totalNode = document.createElement('div');
      totalNode.className = 'oss-patch-total';
      totalNode.innerHTML = '<span>Total</span><strong data-oss-total></strong>';
      panel.appendChild(totalNode);
    }

    function updateTotal() {
      if (!totalNode) return;
      const extras = selections.reduce((sum, item) => sum + (item.checkbox.checked ? item.price : 0), 0);
      const target = qs('[data-oss-total]', totalNode);
      if (target) target.textContent = formatMoney(variantPrice() + extras);
    }

    anchor.insertAdjacentElement('afterend', panel);
    form.addEventListener('change', () => window.setTimeout(updateTotal, 0));
    new MutationObserver(updateTotal).observe(form, { subtree: true, attributes: true, attributeFilter: ['value'] });
    updateTotal();
  };

  const detailIcon = (typeName) => {
    if (typeName === 'shirt') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 4 4 2 4-2 5 3-3 5-2-1v9H8v-9l-2 1-3-5 5-3Z" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    if (typeName === 'user') return '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M5 21c.5-5 3-7 7-7s6.5 2 7 7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="m12 4 2 5 5 .5-4 3.2 1.2 5.3-4.2-2.7L7.8 18 9 12.7 5 9.5 10 9l2-5Z" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>';
  };

  const addProductDetails = () => {
    const details = config.details || {};
    const rows = [
      ['shirt', 'Type of jersey', details.jerseyType],
      ['user', 'Age', details.ageGroup],
      ['design', 'Official name & number design', details.nameNumberDesign]
    ].filter(row => normalise(row[2]));
    if (!rows.length || qs('.oss-product-details')) return;

    const anchor = qs('.oss-patch-panel') || qs('.oss-size-picker') || qs('.product-detail__options');
    if (!anchor) return;
    const box = document.createElement('div');
    box.className = 'oss-product-details';
    const heading = document.createElement('div');
    heading.className = 'oss-details-heading';
    heading.textContent = 'About the product';
    box.appendChild(heading);

    rows.forEach(([iconName, label, value]) => {
      const row = document.createElement('div');
      row.className = 'oss-detail-row';
      const icon = document.createElement('div');
      icon.className = 'oss-detail-icon';
      icon.innerHTML = detailIcon(iconName);
      const copy = document.createElement('div');
      copy.className = 'oss-detail-copy';
      const small = document.createElement('span');
      small.textContent = `${label}:`;
      const strong = document.createElement('strong');
      strong.textContent = normalise(value).toUpperCase();
      copy.append(small, strong);
      row.append(icon, copy);
      box.appendChild(row);
    });
    anchor.insertAdjacentElement('afterend', box);
  };

  const soundIcon = (muted) => muted
    ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6.5 9H3v6h3.5L11 19V5Z"/><path d="m17 9 4 4m0-4-4 4"/></svg><span>Sound</span>'
    : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6.5 9H3v6h3.5L11 19V5Z"/><path d="M15 9.5c1 .8 1.5 1.7 1.5 2.5S16 13.7 15 14.5M18 7c2 1.4 3 3.1 3 5s-1 3.6-3 5"/></svg><span>Mute</span>';

  const prepareNativeVideo = (video) => {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('preload', 'auto');
  };

  const makeNativeSoundButton = (video) => {
    const wrapper = video.closest('.product-media--video');
    if (!wrapper || qs('.oss-video-sound-toggle', wrapper)) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'oss-video-sound-toggle';
    const sync = () => {
      button.innerHTML = soundIcon(video.muted || video.volume === 0);
      button.setAttribute('aria-label', video.muted || video.volume === 0 ? 'Turn sound on' : 'Mute video');
    };
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      video.muted = !video.muted;
      if (!video.muted && video.volume === 0) video.volume = 1;
      video.dataset.ossIntentPlay = '1';
      if (video.paused) {
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      }
      sync();
    });
    video.addEventListener('volumechange', sync);
    sync();
    wrapper.appendChild(button);
  };

  const ensureExternalApi = (iframe) => {
    if (!iframe || iframe.dataset.ossApiReady === '1') return;
    try {
      const url = new URL(iframe.src, window.location.href);
      if (/youtube\.com|youtu\.be/i.test(url.hostname)) {
        url.searchParams.set('enablejsapi', '1');
        url.searchParams.set('playsinline', '1');
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('mute', '1');
      }
      iframe.src = url.toString();
      iframe.dataset.ossApiReady = '1';
    } catch (e) {}
  };

  const externalCommand = (iframe, command) => {
    if (!iframe || !iframe.contentWindow) return;
    const src = iframe.src || '';
    if (/youtube\.com|youtu\.be/i.test(src)) {
      const func = command === 'play' ? 'playVideo' : command === 'pause' ? 'pauseVideo' : command === 'mute' ? 'mute' : 'unMute';
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
    } else if (/vimeo\.com/i.test(src)) {
      if (command === 'play' || command === 'pause') iframe.contentWindow.postMessage({ method: command }, '*');
      else iframe.contentWindow.postMessage({ method: 'setVolume', value: command === 'mute' ? 0 : 1 }, '*');
    }
  };

  const makeExternalSoundButton = (frameWrapper) => {
    const iframe = qs('iframe', frameWrapper);
    if (!iframe || qs('.oss-video-sound-toggle', frameWrapper)) return;
    ensureExternalApi(iframe);
    let muted = true;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'oss-video-sound-toggle';
    const sync = () => {
      button.innerHTML = soundIcon(muted);
      button.setAttribute('aria-label', muted ? 'Turn sound on' : 'Mute video');
    };
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      muted = !muted;
      externalCommand(iframe, muted ? 'mute' : 'unmute');
      externalCommand(iframe, 'play');
      sync();
    });
    sync();
    frameWrapper.appendChild(button);
  };

  const mediaNodeForId = (id) => {
    if (!id) return null;
    return qsa('[data-product-media]').find(node => String(node.getAttribute('data-media-id')) === String(id)) || null;
  };

  const activeMediaId = () => {
    const activeThumb = qs('[data-product-media-thumbnail].thumb-active');
    if (activeThumb && activeThumb.dataset.mediaId) return activeThumb.dataset.mediaId;
    const current = qs('.product-detail__images .slick-current [data-product-media]');
    if (current) return current.getAttribute('data-media-id');
    const featured = qs('.product-detail__images[data-featured-media-id]');
    return featured && featured.dataset.featuredMediaId;
  };

  let intendedMediaId = null;
  let switchingMedia = false;

  const stopInactiveMedia = (activeId) => {
    qsa('video.media-video').forEach(video => {
      const media = video.closest('[data-product-media]');
      const id = media && media.getAttribute('data-media-id');
      if (!activeId || String(id) !== String(activeId)) {
        video.dataset.ossIntentPlay = '0';
        if (!video.paused) video.pause();
      }
    });

    qsa('.product-media[data-video-type="external"], .product-media--video[data-video-type="external"]').forEach(wrapper => {
      const media = wrapper.closest('[data-product-media]');
      const id = media && media.getAttribute('data-media-id');
      if (!activeId || String(id) !== String(activeId)) {
        externalCommand(qs('iframe', wrapper), 'pause');
      }
    });
  };

  const playMediaById = (id) => {
    if (!id) return;
    intendedMediaId = String(id);
    switchingMedia = true;
    stopInactiveMedia(intendedMediaId);

    const media = mediaNodeForId(intendedMediaId);
    if (!media) {
      switchingMedia = false;
      return;
    }

    const video = qs('video.media-video', media);
    if (video) {
      prepareNativeVideo(video);
      makeNativeSoundButton(video);
      video.dataset.ossIntentPlay = '1';
      const promise = video.play();
      if (promise && promise.catch) promise.catch(() => {});
      window.setTimeout(() => { switchingMedia = false; }, 120);
      return;
    }

    const external = qs('.product-media[data-video-type="external"], .product-media--video[data-video-type="external"]', media);
    if (external) {
      makeExternalSoundButton(external);
      const iframe = qs('iframe', external);
      ensureExternalApi(iframe);
      externalCommand(iframe, 'mute');
      externalCommand(iframe, 'play');
    }
    window.setTimeout(() => { switchingMedia = false; }, 120);
  };

  const syncToVisibleMedia = () => {
    const id = activeMediaId();
    if (!id) return;
    playMediaById(id);
  };

  const guardNativePlayback = (video) => {
    if (video.dataset.ossPlaybackGuard === '1') return;
    video.dataset.ossPlaybackGuard = '1';
    prepareNativeVideo(video);
    makeNativeSoundButton(video);

    let resumeTimer = null;
    video.addEventListener('pause', () => {
      window.clearTimeout(resumeTimer);
      if (switchingMedia || video.dataset.ossIntentPlay !== '1' || video.ended || document.visibilityState === 'hidden') return;
      resumeTimer = window.setTimeout(() => {
        if (video.dataset.ossIntentPlay !== '1' || video.ended || document.visibilityState === 'hidden') return;
        const promise = video.play();
        if (promise && promise.catch) promise.catch(() => {});
      }, 90);
    });
  };

  const initVideoEnhancements = () => {
    const hasVideo = qs('video.media-video, .product-media[data-video-type="external"], .product-media--video[data-video-type="external"]');
    if (!hasVideo) return;

    qsa('video.media-video').forEach(guardNativePlayback);
    qsa('.product-media[data-video-type="external"], .product-media--video[data-video-type="external"]').forEach(makeExternalSoundButton);

    document.addEventListener('click', event => {
      const thumb = event.target.closest('[data-product-media-thumbnail]');
      if (thumb && thumb.dataset.mediaId) {
        intendedMediaId = String(thumb.dataset.mediaId);
        playMediaById(intendedMediaId);
        window.setTimeout(() => playMediaById(intendedMediaId), 160);
        window.setTimeout(() => playMediaById(intendedMediaId), 420);
        return;
      }

      if (event.target.closest('.slick-arrow, .slick-dots button')) {
        intendedMediaId = null;
        switchingMedia = true;
        window.setTimeout(() => {
          switchingMedia = false;
          syncToVisibleMedia();
        }, 380);
      }
    });

    if (window.jQuery) {
      try {
        window.jQuery('.product-detail__images').off('afterChange.ossVideo').on('afterChange.ossVideo', () => {
          const id = activeMediaId();
          if (id) playMediaById(id);
        });
      } catch (e) {}
    }

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && intendedMediaId) {
        window.setTimeout(() => playMediaById(intendedMediaId), 80);
      }
    });

    window.setTimeout(() => {
      const id = activeMediaId();
      if (id) playMediaById(id);
    }, 420);
  };

  const initJerseyUi = () => {
    if (!isJersey) return;
    document.body.classList.add('oss-jersey-product');
    addClubBanner();
    addMobileTitle();
    buildSizePicker();
    buildPatchPanel();
    addProductDetails();
  };

  const init = () => {
    initJerseyUi();
    initVideoEnhancements();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
